import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogBody, DialogHeader, Icon, Skeleton, Card } from '@repo/ui';
import { addBusinessDays } from 'date-fns';
import type { MultiValue } from 'react-select';
import { z, type ZodIssue } from 'zod';

import type { SelectOption } from '@repo/ui';
import { dateUtil, numberUtil } from '@xtnd/web/shared';

import { FileCategory, MatterInvoiceSummaryFile, Timekeeper } from '../../../types';
import {
  AppealTypeSelection,
  AppealForm,
  type AppealFormData,
  type TimekeeperOption,
} from './index';
import { TIMEKEEPER_COLORS } from './AppealForm';
import {
  useUploadMatterInvoiceSummaryFiles,
  useMatterInvoiceSummaryFilesQuery,
} from '../../../queries/mattersQuery';
import { AppealType, AppealStatus, APPEAL_STATUS_LABEL, WriteOffType } from '@repo/types';
import { get, isEmpty, size } from 'lodash';
import { useGetInvoice } from '../../../queries/invoiceQuery';
import { useWriteOffItems } from '../../../queries/writeOffsQuery';
import {
  buildEditReconcileWarningLines,
  type WriteOffEditImpact,
} from '../../../utils/editReconcileWarning';
import { AppealSummarySkeleton } from '../../appeals/AppealSummary';

export interface MarkToBeAppealedModalProps {
  isOpen: boolean;
  invoiceId: string;
  invoiceNumber?: string;
  isLoading?: boolean;
  initialAppealType?: AppealType;
  isEditMode?: boolean;
  setLoading?: (loading: { toBeAppealed: boolean }) => void;
  onSubmit: (payload: MarkToBeAppealedSubmitData) => Promise<void>;
  onClose: (success?: boolean, appealType?: AppealType) => void;
}

export interface MarkToBeAppealedSubmitData {
  appealType: AppealType;
  feesCut: number;
  expensesCut: number;
  promptPayment: number;
  bulkDiscount: number;
  administrativeFee: number;
  comment: string;
  deadline?: string;
  attyDeadline?: string;
  timekeepers?: { id: string; email: string; name: string }[];
  files: MatterInvoiceSummaryFile[];
  appealedFees?: number;
  appealedExpenses?: number;
}

const initialFormData: AppealFormData = {
  promptPayment: '0',
  bulkDiscount: '0',
  administrativeFee: '0',
  appealedFees: '0',
  appealedExpenses: '0',
  feesCut: '0',
  expensesCut: '0',
  comment: '',
  deadline: null,
  attyDeadline: null,
  timekeepers: null,
  files: [],
};

const numericSchemaAllowZero = (fieldLabel: string) =>
  z
    .string({ required_error: `${fieldLabel} is required` })
    .trim()
    .transform((value: string) => Number(value))
    .refine((value: number) => !Number.isNaN(value), {
      message: `${fieldLabel} must be a number`,
    })
    .refine((value: number) => value >= 0, {
      message: `${fieldLabel} must be greater than or equal to 0`,
    });

const numericSchema = (fieldLabel: string) =>
  z
    .string({ required_error: `${fieldLabel} is required` })
    .trim()
    .min(0, { message: `${fieldLabel} must be greater than 0` })
    .transform((value: string) => Number(value))
    .refine((value: number) => !Number.isNaN(value), {
      message: `${fieldLabel} must be a number`,
    });

const baseAppealSchema = z.object({
  promptPayment: numericSchemaAllowZero('Prompt payment'),
  bulkDiscount: numericSchemaAllowZero('Bulk discount'),
  administrativeFee: numericSchemaAllowZero('Administrative fee'),
  feesCut: numericSchema('Fees cut'),
  expensesCut: numericSchema('Expenses cut'),
  appealedFees: numericSchema('Appealed fees'),
  appealedExpenses: numericSchema('Appealed expenses'),
  comment: z.string({ required_error: 'Comment is required' }).min(1, 'Comment is required'),
  deadline: z.string().nullable(),
  attyDeadline: z.string().nullable(),
  timekeepers: z.union([
    z.array(
      z.object({
        label: z.string(),
        value: z.string(),
        color: z.string().optional(),
        id: z.string(),
      })
    ),
    z.null(),
  ]),
  files: z.array(z.instanceof(File)),
});

const getAppealSchema = (appealType: AppealType | null, isEditMode = false) => {
  const schema = isEditMode
    ? baseAppealSchema.partial({ comment: true })
    : baseAppealSchema;

  if (appealType === AppealType.SUBSEQUENT) {
    return schema.extend({
      deadline: z.string().min(1, 'Client Appeal Deadline is required'),
      attyDeadline: z.string().min(1, 'Atty Response Deadline is required'),
      timekeepers: z
        .union([
          z.array(
            z.object({
              label: z.string(),
              value: z.string(),
              color: z.string().optional(),
              id: z.string(),
            })
          ),
          z.null(),
        ])
        .refine(
          (val: string | any[] | null) => val !== null && Array.isArray(val) && val.length > 0,
          {
            message: 'Timekeepers is required',
          }
        ),
    });
  }

  return schema;
};

const BALANCE_FIELDS = [
  'feesCut',
  'expensesCut',
  'promptPayment',
  'bulkDiscount',
  'administrativeFee',
] as const;

// Validation error messages
const ERROR_MESSAGES = {
  FEES_AND_EXPENSES_BOTH_ZERO: 'Either Fees Cut or Expenses Cut must be greater than 0',
  APPEALED_BOTH_ZERO: 'The appealed fees and expenses cannot both be 0.',
  APPEALED_FEES_EXCEEDS: (max: number) =>
    `The appealed fees cannot be greater than ${numberUtil.formatCurrency(max)}.`,
  APPEALED_EXPENSES_EXCEEDS: (max: number) =>
    `The appealed expenses cannot be greater than ${numberUtil.formatCurrency(max)}.`,
  TOTAL_EXCEEDS_BALANCE: (balance: number) =>
    `The total write-offs and the amount to be appealed must be less than or equal to ${numberUtil.formatCurrency(balance)}.`,
} as const;

// Validation helper functions
const validateCutFields = (
  feesCut: number,
  expensesCut: number
): { feesCut?: string; expensesCut?: string } => {
  if (feesCut === 0 && expensesCut === 0) {
    return {
      feesCut: ERROR_MESSAGES.FEES_AND_EXPENSES_BOTH_ZERO,
      expensesCut: ERROR_MESSAGES.FEES_AND_EXPENSES_BOTH_ZERO,
    };
  }
  return {};
};

const validateAppealedFields = (
  appealedFees: number,
  appealedExpenses: number,
  feesCut: number,
  expensesCut: number
): { appealedFees?: string; appealedExpenses?: string } => {
  const errors: { appealedFees?: string; appealedExpenses?: string } = {};

  if (appealedFees === 0 && appealedExpenses === 0) {
    errors.appealedFees = ERROR_MESSAGES.APPEALED_BOTH_ZERO;
  }

  if (appealedFees > feesCut) {
    errors.appealedFees = ERROR_MESSAGES.APPEALED_FEES_EXCEEDS(feesCut);
  }

  if (appealedExpenses > expensesCut) {
    errors.appealedExpenses = ERROR_MESSAGES.APPEALED_EXPENSES_EXCEEDS(expensesCut);
  }

  return errors;
};

const validateTotalBalance = (
  formData: AppealFormData,
  currentField: string,
  currentValue: number,
  actualBalance: number
): string | null => {
  const otherFields = BALANCE_FIELDS.filter(f => f !== currentField);
  const otherTotal = otherFields.reduce(
    (sum, field) => sum + Number(formData[field as keyof AppealFormData] || 0),
    0
  );
  const total = Number((otherTotal + currentValue).toFixed(2));
  if (total > actualBalance) {
    return ERROR_MESSAGES.TOTAL_EXCEEDS_BALANCE(actualBalance);
  }

  return null;
};

/**
 * Skeleton component for loading state when invoice data is being fetched
 */
const AppealFormSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col lg:flex-row gap-6 pt-4">
      {/* Left Side: Form Skeleton */}
      <div className="flex-1 space-y-6">
        {/* Financial Information Section Skeleton */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <Skeleton className="w-10 h-10 rounded-lg mr-4" />
              <div>
                <Skeleton className="h-6 w-48 mb-2" />
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left Column: Input Fields Skeleton */}
            <div className="flex flex-col gap-4">
              {/* Appeal Categories */}
              <Skeleton className="h-4 w-40 mb-2" />
              <div className="space-y-4">
                <div>
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-10 w-full" />
                </div>
                <div>
                  <Skeleton className="h-4 w-28 mb-2" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </div>

              {/* Write-offs */}
              <div className="space-y-4 pt-2">
                <Skeleton className="h-4 w-32 mb-2" />
                <div className="flex flex-col gap-4">
                  <div>
                    <Skeleton className="h-4 w-32 mb-2" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                  <div>
                    <Skeleton className="h-4 w-28 mb-2" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                  <div>
                    <Skeleton className="h-4 w-24 mb-2" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Summary Cards Skeleton */}
            <div className="flex flex-col justify-start space-y-6">
              {/* Amount to Be Appealed Card */}
              <Card className="rounded-xl p-5 shadow-sm">
                <Skeleton className="h-4 w-40 mb-2" />
                <Skeleton className="h-10 w-32 mb-2" />
                <Skeleton className="h-3 w-full mt-2" />
                <Skeleton className="h-3 w-3/4 mt-1" />
              </Card>

              {/* Agreed to Pay Card */}
              <Card className="rounded-xl p-5 shadow-sm">
                <Skeleton className="h-4 w-36 mb-2" />
                <Skeleton className="h-10 w-32 mb-2" />
                <Skeleton className="h-3 w-full mt-2" />
                <Skeleton className="h-3 w-4/5 mt-1" />
              </Card>

              {/* Logic Summary Card */}
              <Card className=" rounded-xl p-5">
                <Skeleton className="h-5 w-32 mb-4" />
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Skeleton className="h-3 w-48" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                  <div className="flex justify-between">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                  <div className="flex justify-between pt-2 mt-2">
                    <Skeleton className="h-3 w-32" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                  <Skeleton className="h-3 w-full mt-2" />
                </div>
              </Card>
            </div>
          </div>
        </Card>

        {/* Submission Details Section Skeleton */}
        <Card className="p-6">
          <div className="flex items-center mb-4">
            <Skeleton className="w-10 h-10 rounded-lg mr-4" />
            <Skeleton className="h-6 w-40" />
          </div>
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-24 w-full mb-4" />
          <Skeleton className="h-4 w-32 mb-2" />
          <Skeleton className="h-32 w-full" />
        </Card>
      </div>

      {/* Right Side: Invoice Summary Sidebar Skeleton */}
      <div className="lg:w-80 space-y-6">
        <AppealSummarySkeleton />
        <Card className="p-6 rounded-2xl shadow-xl sticky top-6">
          <Skeleton className="h-6 w-40 mb-4" />

          <div className="space-y-4">
            <div className="flex justify-between">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-16" />
            </div>
            <div className="flex justify-between pt-1">
              <Skeleton className="h-5 w-28" />
              <Skeleton className="h-5 w-24" />
            </div>
          </div>

          <div className="mt-4 pt-3 border-t border-xtnd-dark-200 dark:border-xtnd-dark-700 space-y-4">
            <div className="flex justify-between items-center">
              <Skeleton className="h-3 w-36" />
              <Skeleton className="h-4 w-20" />
            </div>

            <div className="p-3 rounded-xl shadow-sm">
              <div className="flex justify-between items-center mb-1">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-5 w-20" />
              </div>
              <Skeleton className="h-1.5 w-full rounded-full my-2" />
            </div>
          </div>

          <Skeleton className="h-10 w-full rounded-md mt-6" />
        </Card>
      </div>
    </div>
  );
};

/**
 * Shared modal component for Mark To Be Appealed functionality.
 * Used by both appeals and matter-detail modules.
 */
export const MarkToBeAppealedModal: React.FC<MarkToBeAppealedModalProps> = ({
  isOpen,
  invoiceId,
  invoiceNumber: invoiceNumberProp,
  isLoading: externalIsLoading,
  initialAppealType,
  isEditMode = false,
  onSubmit,
  setLoading: externalSetLoading,
  onClose,
}) => {
  const { data: invoice, isLoading: isInvoiceLoading } = useGetInvoice(invoiceId, {
    enabled: isOpen,
  });

  const { mutateAsync: uploadFiles } = useUploadMatterInvoiceSummaryFiles(invoiceId, 'AR Appeal', {
    isShowToast: false,
    category: FileCategory.PAYMENT_SUPPORT,
  });

  const {
    data: existingFilesResponse,
    isFetching: isFetchingExistingFiles,
    isLoading: isLoadingExistingFiles,
  } = useMatterInvoiceSummaryFilesQuery(invoiceId, { enabled: isOpen });

  // Write-off rows for the active appeal — feed the reconcile warning (edit mode only).
  const { data: writeOffItemsData } = useWriteOffItems(invoiceId, {
    enabled: isOpen && isEditMode,
  });
  const editAppealId = get(invoice, 'appeal.appealId');
  const editWriteOffItems = useMemo(
    () => (writeOffItemsData?.data ?? []).filter(item => item.appealId === editAppealId),
    [writeOffItemsData, editAppealId]
  );

  // Use internal loading state if external is not provided
  const [internalIsLoading, setInternalIsLoading] = useState(false);
  const isLoading = externalIsLoading ?? internalIsLoading;
  const setLoading = useCallback(
    (loading: { toBeAppealed: boolean }) => {
      if (externalSetLoading) {
        externalSetLoading(loading);
      } else {
        setInternalIsLoading(loading.toBeAppealed);
      }
    },
    [externalSetLoading]
  );

  const isPartiallyPaid = get(invoice, 'paid', 0) > 0;
  const outstandingBalance = get(invoice, 'outstandingBalance', 0);
  const totalAmount = get(invoice, 'total', 0);
  const amountPaid = get(invoice, 'paid', 0);
  const creditsIssued = get(invoice, 'creditsIssued', 0);
  const timekeepers = get(invoice, 'timekeepers', []) as Timekeeper[];
  const invoiceNumber = invoiceNumberProp ?? get(invoice, 'invoiceNumber', '');

  const [selectedAppealType, setSelectedAppealType] = useState<AppealType | null>(null);
  const [formData, setFormData] = useState<AppealFormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof AppealFormData, string>>>({});
  const [selectedExistingFiles, setSelectedExistingFiles] = useState<MatterInvoiceSummaryFile[]>(
    []
  );
  const invoiceStatus = isPartiallyPaid ? 'Partially Paid' : 'Fully Paid';

  const existingFiles = existingFilesResponse?.data || [];

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setSelectedAppealType(null);
      setFormData(initialFormData);
      setErrors({});
      setSelectedExistingFiles([]);
    }
  }, [isOpen]);

  // Pre-fill form data from existing appeal when in edit mode
  useEffect(() => {
    if (isOpen && isEditMode && !isEmpty(invoice)) {
      const appeal = get(invoice, 'appeal', {});
      const appealType = get(appeal, 'appealType');
      setSelectedAppealType(appealType || AppealType.SUBSEQUENT);

      // Color index must come from the full invoice timekeepers list, not the selected subset.
      const allTimekeepers = get(invoice, 'timekeepers', []) as Timekeeper[];
      const existingTimekeepers = get(appeal, 'timekeepers', []) as Timekeeper[];
      const timekeeperSelectOptions: TimekeeperOption[] = existingTimekeepers.map((t) => {
        const fullListIndex = allTimekeepers.findIndex(tk => tk.id === t.id);
        const colorIndex = fullListIndex >= 0 ? fullListIndex : 0;
        const colorConfig = TIMEKEEPER_COLORS[colorIndex % TIMEKEEPER_COLORS.length]!;
        return {
          value: t.email,
          label: t.name,
          id: t.id,
          color: colorConfig.color,
          bgColor: colorConfig.bgColor,
        };
      });

      setFormData({
        feesCut: String(get(appeal, 'feesCut', 0)),
        expensesCut: String(get(appeal, 'expensesCut', 0)),
        // Prefill the discount triplet from BE-derived `appeal.discountsFromRows` (the edit
        // source of truth); the legacy denormalized `appeal.{...}` map can drift.
        promptPayment: String(get(appeal, 'discountsFromRows.promptPayment', 0)),
        bulkDiscount: String(get(appeal, 'discountsFromRows.bulkDiscount', 0)),
        administrativeFee: String(get(appeal, 'discountsFromRows.administrativeFee', 0)),
        appealedFees: String(get(appeal, 'appealedFees', 0)),
        appealedExpenses: String(get(appeal, 'appealedExpenses', 0)),
        comment: isEditMode ? get(invoice, 'comment', '') : '',
        deadline: get(appeal, 'deadline', null),
        attyDeadline: get(appeal, 'attyDeadline', null),
        timekeepers: timekeeperSelectOptions.length > 0 ? timekeeperSelectOptions : null,
        files: [],
      });
    }
  }, [isOpen, isEditMode, invoice]);

  useEffect(() => {
    if (isOpen && initialAppealType && !isEmpty(invoice) && !isEditMode) {
      handleTypeSelect(initialAppealType);
    }
  }, [isOpen, initialAppealType, invoice, isEditMode]);

  const handleTypeSelect = (type: AppealType) => {
    setSelectedAppealType(type);
    setErrors({});
    // Set default deadline for subsequent appeals
    if (type === AppealType.SUBSEQUENT) {
      const defaultAttyDeadline = addBusinessDays(new Date(), 2);
      const defaultClientDeadline = addBusinessDays(new Date(), 10);
      setFormData(prev => ({
        ...prev,
        attyDeadline: dateUtil.formatDate(defaultAttyDeadline, dateUtil.DateFormat.US),
        deadline: dateUtil.formatDate(defaultClientDeadline, dateUtil.DateFormat.US),
      }));
    }
  };

  useEffect(() => {
    if (isOpen && !isEmpty(invoice) && !initialAppealType && !isEditMode) {
      handleTypeSelect(AppealType.SUBSEQUENT);
    }
  }, [isOpen, invoice, initialAppealType, isEditMode]);

  const handleBack = () => {
    if (initialAppealType) {
      handleClose(false);
      return;
    }
    setSelectedAppealType(null);
    setFormData(initialFormData);
    setErrors({});
  };

  const handleInputChange = (field: keyof AppealFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const actualBalance = useMemo(() => {
    let balance =
      totalAmount -
      get(invoice, 'writeOffAmount', 0) -
      get(invoice, 'appeal.lastCollectedAmount', 0);

    if (isEditMode && invoice) {
      const appeal = get(invoice, 'appeal', {});
      // Add back the prefilled discounts so the form can redistribute them (from the
      // drift-free `discountsFromRows`, as in the prefill above).
      balance += Number(get(appeal, 'discountsFromRows.promptPayment', 0));
      balance += Number(get(appeal, 'discountsFromRows.bulkDiscount', 0));
      balance += Number(get(appeal, 'discountsFromRows.administrativeFee', 0));
    }

    return balance;
  }, [totalAmount, invoice, isEditMode]);

  // Reconcile warning for the wide edit (TBA / AAS / AS-Backlog): the discount triplet is the only
  // write-off the BE mutates here (cuts live on the appeal record, not a write-off row) — each
  // discount stays Approved on a non-zero change and is removed at 0. Same shared helper as the
  // other edit forms; cuts / Write-Off Remaining impacts are intentionally omitted for these statuses.
  const round2 = (n: number) => Number(Number(n || 0).toFixed(2));
  const editCurrentStatus = get(invoice, 'appealStatus') as AppealStatus | undefined;
  const findDiscountRow = (type: WriteOffType) =>
    editWriteOffItems.find(
      w => w.type === type && !w.isWriteOffRemaining && w.fromAppealStatus === editCurrentStatus
    ) ?? editWriteOffItems.find(w => w.type === type && !w.isWriteOffRemaining);
  const discountImpact = (
    label: string,
    type: WriteOffType,
    value: string
  ): WriteOffEditImpact => {
    const row = findDiscountRow(type);
    return {
      label,
      changed: round2(Number(value)) !== round2(row?.amount ?? 0),
      newAmount: round2(Number(value)),
      status: row?.status,
      hasRow: !!row,
      reapproveIfArchived: true,
      deleteOnZero: true,
    };
  };
  // Edit trust gate: when the active appeal's write-offs lack recorded lineage
  // (writeOffOriginsKnown === false), an edit could mutate the wrong row — render read-only +
  // reason banner. The BE editAppeal endpoint also rejects (422) as the backstop. Backfill stamps
  // fromAppealStatus for these deterministic-flow statuses, so this normally never fires; it
  // guards drifted / un-backfilled legacy rows (defense-in-depth, mirrors MarkPartialWriteOff).
  const editBlocked = isEditMode && get(invoice, 'writeOffOriginsKnown') === false;

  const editWarningLines = buildEditReconcileWarningLines(
    isEditMode && !editBlocked
      ? [
          discountImpact('Prompt Payment', WriteOffType.PROMPT_PAYMENT, formData.promptPayment),
          discountImpact('Bulk Discount', WriteOffType.BULK_DISCOUNT, formData.bulkDiscount),
          discountImpact('Admin Fee', WriteOffType.ADMIN_FEE, formData.administrativeFee),
        ]
      : []
  );

  const handleNumberChange = (field: keyof AppealFormData, value: string) => {
    const normalized = numberUtil.normalizeNumericString(value);
    const AMOUNT_REGEX = /^\d*\.?\d{0,2}$/;

    if (!AMOUNT_REGEX.test(normalized)) {
      return;
    }

    const finalValue = normalized === '' ? '0' : normalized;
    const numericValue = Number(finalValue);

    setFormData(prev => ({ ...prev, [field]: finalValue }));
    setErrors(prev => ({ ...prev, [field]: '' }));

    // Get current form values with the new value applied
    const currentFeesCut = field === 'feesCut' ? numericValue : Number(formData.feesCut || 0);
    const currentExpensesCut =
      field === 'expensesCut' ? numericValue : Number(formData.expensesCut || 0);
    const currentAppealedFees =
      field === 'appealedFees' ? numericValue : Number(formData.appealedFees || 0);
    const currentAppealedExpenses =
      field === 'appealedExpenses' ? numericValue : Number(formData.appealedExpenses || 0);

    // Validate cut fields. Empty input is normalized to 0 above; a 0 cut is valid while the other
    // is > 0, so there is no per-field "required" — both-zero is the only constraint, surfaced on
    // BOTH cut fields (and cleared on both once resolved).
    if (field === 'feesCut' || field === 'expensesCut') {
      const cutErrors = validateCutFields(currentFeesCut, currentExpensesCut);
      if (cutErrors.feesCut || cutErrors.expensesCut) {
        setErrors(prev => ({
          ...prev,
          feesCut: cutErrors.feesCut ?? '',
          expensesCut: cutErrors.expensesCut ?? '',
        }));
        return;
      }
      setErrors(prev => ({ ...prev, feesCut: '', expensesCut: '' }));
    }

    // Validate appealed fields
    if (field === 'appealedFees' || field === 'appealedExpenses') {
      const appealedErrors = validateAppealedFields(
        currentAppealedFees,
        currentAppealedExpenses,
        currentFeesCut,
        currentExpensesCut
      );
      if (appealedErrors[field]) {
        setErrors(prev => ({ ...prev, [field]: appealedErrors[field] }));
        return;
      }
    }

    // Validate total balance
    if (BALANCE_FIELDS.includes(field as (typeof BALANCE_FIELDS)[number])) {
      const balanceError = validateTotalBalance(formData, field, numericValue, actualBalance);
      if (balanceError) {
        // Clear all balance field errors first
        const clearedErrors = BALANCE_FIELDS.reduce(
          (acc, f) => ({ ...acc, [f]: '' }),
          {} as Record<string, string>
        );
        setErrors(prev => ({ ...prev, ...clearedErrors, [field]: balanceError }));
      }
    }
  };

  const handleDateChange = (field: keyof AppealFormData, date: Date | null) => {
    if (!date) {
      setErrors(prev => ({ ...prev, [field]: 'This field is required' }));
      return;
    }

    const formattedDate = dateUtil.formatDate(date, dateUtil.DateFormat.US);

    // Create new form data state for comparison
    const newErrors: Partial<Record<keyof AppealFormData, string>> = { [field]: '' };

    // Validate deadlines relationship
    if (field === 'attyDeadline' || field === 'deadline') {
      const attyDate =
        field === 'attyDeadline'
          ? date
          : formData.attyDeadline
            ? new Date(formData.attyDeadline)
            : null;
      const clientDate =
        field === 'deadline' ? date : formData.deadline ? new Date(formData.deadline) : null;

      if (attyDate && clientDate) {
        if (attyDate > clientDate) {
          // If we changed attyDeadline (Atty), error on attyDeadline
          if (field === 'attyDeadline') {
            newErrors.attyDeadline =
              'Atty Response Deadline cannot be after Client Appeal Deadline';
          }
          // If we changed deadline (Client), error on deadline
          else {
            newErrors.deadline = 'Client Appeal Deadline cannot be before Atty Response Deadline';
          }
        } else {
          // Clear comparison errors if relationship is now valid
          if (errors.attyDeadline?.includes('Atty Response Deadline cannot be after')) {
            newErrors.attyDeadline = '';
          }
          if (errors.deadline?.includes('Client Appeal Deadline cannot be before')) {
            newErrors.deadline = '';
          }
        }
      }
    }

    setFormData(prev => ({ ...prev, [field]: formattedDate }));
    setErrors(prev => ({ ...prev, ...newErrors }));
  };

  const handleSelectChange = (
    field: keyof AppealFormData,
    value: MultiValue<SelectOption> | null
  ) => {
    setFormData(prev => ({ ...prev, [field]: value ? [...value] : null }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleFilesChange = useCallback(
    (files: File[]) => {
      setFormData(prev => ({ ...prev, files }));
      // Clear error if either new files or existing files are selected
      const hasFiles = files.length > 0 || selectedExistingFiles.length > 0;
      setErrors(prev => ({
        ...prev,
        files: hasFiles ? '' : 'At least one attachment is required',
      }));
    },
    [selectedExistingFiles.length]
  );

  const handleExistingFilesChange = useCallback(
    (files: MatterInvoiceSummaryFile[]) => {
      setSelectedExistingFiles(files);
      // Clear error if either new files or existing files are selected
      const hasFiles = formData.files.length > 0 || files.length > 0;
      setErrors(prev => ({
        ...prev,
        files: hasFiles ? '' : 'At least one attachment is required',
      }));
    },
    [formData.files.length]
  );

  const handleSubmit = async () => {
    try {
      setLoading({ toBeAppealed: true });

      const { feesCut, expensesCut, appealedFees, appealedExpenses } = formData;
      const numericFeesCut = Number(feesCut || 0);
      const numericExpensesCut = Number(expensesCut || 0);
      const numericAppealedFees = Number(appealedFees || 0);
      const numericAppealedExpenses = Number(appealedExpenses || 0);

      // Collect all validation errors
      const validationErrors: Partial<Record<keyof AppealFormData, string>> = {};

      // Validate cut fields
      const cutErrors = validateCutFields(numericFeesCut, numericExpensesCut);
      Object.assign(validationErrors, cutErrors);

      // Validate appealed fields for BACKLOG type
      if (selectedAppealType === AppealType.BACKLOG) {
        const appealedErrors = validateAppealedFields(
          numericAppealedFees,
          numericAppealedExpenses,
          numericFeesCut,
          numericExpensesCut
        );
        Object.assign(validationErrors, appealedErrors);
      }

      // Backstop the BE cap (cuts + discounts <= balance). Inline handleNumberChange already
      // flags this per-keystroke, but a type switch clears errors without clearing values — so
      // re-check at submit. Without it the BE rejects with a generic "Collected amount is not valid."
      const balanceTotal = Number(
        BALANCE_FIELDS.reduce(
          (sum, f) => sum + Number(formData[f as keyof AppealFormData] || 0),
          0
        ).toFixed(2)
      );
      if (balanceTotal > actualBalance) {
        validationErrors.feesCut = ERROR_MESSAGES.TOTAL_EXCEEDS_BALANCE(actualBalance);
      }

      // Validate with Zod schema — in edit mode, comment is optional
      const schema = getAppealSchema(selectedAppealType, isEditMode);
      const result = schema.safeParse(formData);

      if (!result.success) {
        result.error.issues.forEach((issue: ZodIssue) => {
          const field = issue.path[0] as keyof AppealFormData | undefined;
          if (field && !validationErrors[field]) {
            validationErrors[field] = issue.message;
          }
        });
      }

      // Apply all errors and check if any exist
      if (Object.keys(validationErrors).length > 0) {
        setErrors(prev => ({ ...prev, ...validationErrors }));
        return;
      }

      const {
        promptPayment,
        bulkDiscount,
        administrativeFee,
        comment,
        deadline,
        attyDeadline,
        timekeepers: formTimekeepers,
        files,
      } = formData;

      let filesPayLoad = [] as MatterInvoiceSummaryFile[];

      // Include selected existing files
      if (size(selectedExistingFiles) > 0) {
        filesPayLoad = [...filesPayLoad, ...selectedExistingFiles];
      }

      // Upload new files and add them to payload
      if (size(files) > 0) {
        const uploadResult = await uploadFiles({
          files: files,
          successCallback: async response => {
            filesPayLoad = [
              ...filesPayLoad,
              ...(get(response, 'successFiles', []) as MatterInvoiceSummaryFile[]),
            ];
          },
        });

        if (uploadResult.errorFiles && uploadResult.errorFiles.length > 0) {
          return;
        }
      }

      if (size(filesPayLoad) === 0 && !isEditMode) {
        setErrors(prev => ({
          ...prev,
          files: 'At least one attachment is required',
        }));
        return;
      }

      const submitData: MarkToBeAppealedSubmitData = {
        appealType: selectedAppealType as AppealType,
        feesCut: numericFeesCut,
        expensesCut: numericExpensesCut,
        promptPayment: Number(promptPayment || 0),
        bulkDiscount: Number(bulkDiscount || 0),
        administrativeFee: Number(administrativeFee || 0),
        comment,
        files: filesPayLoad,
      };

      if (selectedAppealType === AppealType.SUBSEQUENT) {
        submitData.deadline = dateUtil.formatDate(
          new Date(deadline as string),
          dateUtil.DateFormat.ISO
        );
        submitData.attyDeadline = dateUtil.formatDate(
          new Date(attyDeadline as string),
          dateUtil.DateFormat.ISO
        );
        submitData.timekeepers = ((formTimekeepers ?? []) as TimekeeperOption[]).map(t => ({
          id: t.id,
          email: t.value,
          name: t.label,
        }));
      } else {
        submitData.appealedFees = numericAppealedFees;
        submitData.appealedExpenses = numericAppealedExpenses;
      }

      await onSubmit(submitData);
      handleClose(true);
    } finally {
      setLoading({ toBeAppealed: false });
    }
  };

  const handleClose = (success?: boolean) => {
    if (isLoading) {
      return;
    }

    setSelectedAppealType(null);
    setFormData(initialFormData);
    setErrors({});
    onClose(success ?? false, selectedAppealType as AppealType);
  };

  const renderDialogContent = () => {
    if (isInvoiceLoading) {
      return <AppealFormSkeleton />;
    }

    if (!selectedAppealType) {
      return (
        <AppealTypeSelection
          invoiceNumber={invoiceNumber}
          invoiceStatus={invoiceStatus}
          selectedAppealType={selectedAppealType}
          onSelectAppealType={handleTypeSelect}
        />
      );
    }

    return (
      <AppealForm
        responsibleAttorneyName={invoice?.responsibleAttorneyName ?? ''}
        appealType={selectedAppealType}
        isPartiallyPaid={isPartiallyPaid}
        invoiceNumber={invoiceNumber}
        outstandingBalance={outstandingBalance}
        matterDisplayNumber={invoice?.matterDisplayNumber ?? ''}
        clientName={invoice?.clientName ?? ''}
        invoiceDate={invoice?.invoiceDate ?? ''}
        totalAmount={totalAmount}
        amountPaid={amountPaid}
        creditsIssued={creditsIssued}
        timekeepers={timekeepers ?? (get(invoice, 'timekeepers', []) as Timekeeper[])}
        formData={formData}
        errors={errors}
        isLoading={isLoading}
        isEditMode={isEditMode}
        editBlocked={editBlocked}
        editWarningLines={editWarningLines}
        actualBalance={actualBalance}
        onInputChange={handleInputChange}
        onNumberChange={handleNumberChange}
        onDateChange={handleDateChange}
        onSelectChange={handleSelectChange}
        onFilesChange={handleFilesChange}
        onBack={handleBack}
        onSubmit={handleSubmit}
        setError={(field: keyof AppealFormData, error: string) =>
          setErrors(prev => ({ ...prev, [field]: error as string }))
        }
        existingFiles={existingFiles}
        selectedExistingFiles={selectedExistingFiles}
        onExistingFilesChange={handleExistingFilesChange}
        isFetchingExistingFiles={isFetchingExistingFiles || isLoadingExistingFiles}
      />
    );
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      size="xxllg"
      className="!max-h-[95vh] overflow-y-auto"
      showCloseButton={false}
    >
      <DialogHeader className="sticky top-0 bg-white dark:bg-xtnd-dark-800 z-[51]">
        <div className="flex justify-between">
          <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
            {isEditMode
              ? (() => {
                  if (isInvoiceLoading) return '';
                  const status = get(invoice, 'appealStatus', '');
                  const label = get(APPEAL_STATUS_LABEL, status, 'To Be Appealed');
                  if (status === AppealStatus.APPEAL_SUBMITTED) {
                    const at = get(invoice, 'appeal.appealType');
                    if (at === AppealType.BACKLOG) return `Edit - ${label} (Backlog)`;
                    if (at === AppealType.SUBSEQUENT) return `Edit - ${label} (Subsequent)`;
                  }
                  return `Edit - ${label}`;
                })()
              : initialAppealType === AppealType.BACKLOG
                ? 'Appeal Submitted (Backlog)'
                : 'To Be Appealed'}
            {invoiceNumber ? ` - INV #${invoiceNumber}` : ''}
          </h3>
          <button
            type="button"
            className="absolute z-[52] top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-xtnd-blue focus:ring-offset-2 transition-colors dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-xtnd-dark-700"
            onClick={() => handleClose(false)}
            aria-label="Close dialog"
          >
            <Icon name="HiOutlineX" />
          </button>
        </div>
      </DialogHeader>

      <DialogBody className="pt-0 space-y-6">{renderDialogContent()}</DialogBody>
    </Dialog>
  );
};

export default MarkToBeAppealedModal;
